import { useEffect, useState } from "react";
import ScrollToBottom from "react-scroll-to-bottom";
import { VIRTUAL_CONVERSATION_ID } from "../constants";
import { useHelpWidgetContext } from "../context/HelpWidgetContext";
import { useAttachmentsUpload } from "../hooks/useAttachmentsUpload";
import { useConversationMutations } from "../hooks/useConversationMutations";
import { useConversationWebSocket } from "../hooks/useConversationWebSocket";
import { useMarkAsRead } from "../hooks/useMarkAsRead";
import { useShowConversation } from "../hooks/useShowConversation";
import { useRevealingMessageStore } from "../stores/useRevealingMessageStore";
import type { ConversationChannel } from "../types/ConversationChannel";
import type { ConversationStatus } from "../types/ConversationStatus";
import type { HelpWidgetMessage } from "../types/help_widget_message";
import { shouldShowBotProcessing } from "../utils/conversationViewHelpers";
import { ConversationViewAttachments } from "./ConversationViewAttachments";
import { ConversationViewFooter } from "./ConversationViewFooter";
import { ConversationViewLoadingStates } from "./ConversationViewLoadingStates";
import { ConversationViewSkeletonLoader } from "./ConversationViewSkeletonLoader";
import { LeaveQueueConfirmation } from "./LeaveQueueConfirmation";
import { HelpWidgetLayout } from "./common/HelpWidgetLayout";
import { Messages } from "./common/Messages";

export const ConversationView = ({
  conversationId,
}: {
  conversationId: string;
}) => {
  const {
    isShowingLeaveWarning,
    virtualMessages,
    virtualConversationType,
    clearVirtualMessages,
  } = useHelpWidgetContext();
  const isVirtualConversation = conversationId === VIRTUAL_CONVERSATION_ID;
  const { data: conversation, isLoading } = useShowConversation(conversationId);
  const [status, setStatus] = useState<ConversationStatus | null>(null);
  const [channel, setChannel] = useState<ConversationChannel | null>(null);
  const { addMessage, setMessages } = useRevealingMessageStore();
  const [isBotProcessing, setIsBotProcessing] = useState(false);
  const [copilotLoadingText, setCopilotLoadingText] = useState<string | null>(
    null,
  );
  const [isWaitingForConfirmation, setIsWaitingForConfirmation] =
    useState(false);
  // Queue-related state
  const [isWaitingInQueue, setIsWaitingInQueue] = useState(false);
  const [queuePosition, setQueuePosition] = useState<number | null>(null);

  const { markAsRead } = useMarkAsRead();

  useEffect(() => {
    if (isVirtualConversation) {
      setStatus("draft");
      setChannel("circle_bot");
      virtualMessages.forEach(message =>
        addMessage(message as HelpWidgetMessage),
      );
      setIsWaitingInQueue(false);
      setQueuePosition(null);
      setIsBotProcessing(
        shouldShowBotProcessing(virtualMessages as HelpWidgetMessage[]),
      );
    } else if (conversation) {
      setStatus(conversation.status);
      setChannel(conversation.channel || null);
      setMessages(conversation.messages);
      setIsBotProcessing(conversation.is_bot_processing || false);
      setCopilotLoadingText(conversation.copilot_action_loading_text || null);
      setIsWaitingForConfirmation(
        conversation.is_waiting_for_confirmation || false,
      );
      setIsWaitingInQueue(conversation.is_waiting_in_queue || false);
      setQueuePosition(conversation.queue_position || null);
    }
  }, [
    addMessage,
    conversation,
    isVirtualConversation,
    setMessages,
    virtualMessages,
  ]);

  useEffect(() => {
    if (!isVirtualConversation) {
      markAsRead({ conversationId });
    }
  }, [isVirtualConversation, conversationId, markAsRead]);

  const { uploadAttachments, attachments, cancelAttachment, setAttachments } =
    useAttachmentsUpload({ conversationId });

  const { createChatMessage, createChat, clickButton } =
    useConversationMutations({
      channel,
      clearVirtualMessages,
      conversationId,
      setAttachments,
      setIsBotProcessing,
    });

  useConversationWebSocket({
    conversationId,
    isVirtualConversation,
    setChannel,
    setStatus,
    setIsBotProcessing,
    setCopilotLoadingText,
    setIsWaitingForConfirmation,
    setIsWaitingInQueue,
    setQueuePosition,
    markAsRead,
  });

  const onActionButtonClick = (message: HelpWidgetMessage, event: string) => {
    clickButton({ conversationId, event, messageId: message.id });
  };

  const onSendMessage = (text: string) => {
    const attachmentIds = attachments.map(
      attachment => attachment.attachmentId,
    ) as string[];

    if (isVirtualConversation) {
      createChat({
        text,
        virtual_conversation_type: virtualConversationType,
      });
    } else {
      createChatMessage({
        conversationId,
        text,
        attachment_ids: attachmentIds,
      });
    }
  };

  if (isLoading && !isVirtualConversation) {
    return (
      <div className="flex size-full flex-1 flex-col">
        <ConversationViewSkeletonLoader />
      </div>
    );
  }
  if (isShowingLeaveWarning) {
    return <LeaveQueueConfirmation conversationId={conversationId} />;
  }

  return (
    <HelpWidgetLayout className="flex flex-1 flex-col overflow-hidden">
      <ScrollToBottom
        className="flex flex-1 flex-col overflow-hidden"
        scrollViewClassName="scrollbar-hide flex flex-col px-4"
        followButtonClassName="hidden"
        initialScrollBehavior="auto"
        mode="bottom"
      >
        <Messages status={status} onActionButtonClick={onActionButtonClick} />
        <ConversationViewLoadingStates
          channel={channel}
          copilotLoadingText={copilotLoadingText}
          isBotProcessing={isBotProcessing}
          isWaitingForConfirmation={isWaitingForConfirmation}
          isWaitingInQueue={isWaitingInQueue}
          queuePosition={queuePosition}
        />
        <div className="my-4 flex flex-col gap-2">
          {status !== "closed" && (
            <ConversationViewAttachments
              attachments={attachments}
              cancelAttachment={cancelAttachment}
            />
          )}
        </div>
      </ScrollToBottom>
      <ConversationViewFooter
        attachments={attachments}
        channel={channel}
        isBotProcessing={isBotProcessing}
        onSendMessage={onSendMessage}
        status={status}
        sunshineConversationId={conversation?.sunshine_conversation_id}
        uploadAttachments={uploadAttachments}
      />
    </HelpWidgetLayout>
  );
};
